import {
  FormEvent,
  type ChangeEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { prepareRegisterRequestBody } from '../api/assetPayloadNormalize';
import { AssetRequestError } from '../api/getAssetById';
import { formatHttpErrorDetails } from '../api/httpErrorFormat';
import {
  loadGeneralInfoCountries,
  loadGeneralInfoRegions,
  type GeneralInfoPicklistItem,
  type GeneralInfoPicklistsProp,
} from '../api/generalInfoApi';
import { AuthAppleSignInBlock } from '../components/AuthAppleSignInBlock';
import { AuthGoogleSignInBlock } from '../components/AuthGoogleSignInBlock';
import { AssetOwnerBankDetailsSection } from '../components/AssetOwnerBankDetailsSection';
import { DynamicRecordForm } from '../components/DynamicRecordForm';
import { ProfileCompletionUserInfo } from '../components/ProfileCompletionUserInfo';
import type { ClientLegalDocumentId } from '../constants/clientLegalContent';
import {
  createEmptyClientLegalAgreementState,
  isClientLegalAgreementComplete,
  PlatformLegalDocumentsAcceptSection,
  type ClientLegalAgreementState,
} from '../components/PlatformLegalDocumentsAcceptSection';
import { PlatformTermsAcceptSection } from '../components/PlatformTermsAcceptSection';
import { devProxyRouteForApiPath, fetchWithDevProxyFallback } from '../api/fetchWithDevProxyFallback';
import { useAuth } from '../auth/AuthContext';
import { getSessionToken, getStoredLoginIdentifier } from '../auth/sessionToken';
import {
  buildRegisterPostInit,
  buildRegisterSubmitHeadersAsync,
  registerSubmitNeedsSessionAuth,
  stripRegisterWireAuthFields,
} from './registerSubmitAuth';
import {
  BUSINESS_WORKER_REGISTER_OPTION,
  REGISTER_OPTIONS,
  type RegisterOption,
} from './registerOptions';
import {
  canSelectBusinessWorkerRegisterType,
  businessWorkerRegisterUsesBusinessClientSession,
  isBusinessClientLogin,
} from '../auth/userLoginType';
import { deepClone, setAtPath } from '../utils/cloneAndPath';
import { translateLoginOption, useI18n, type MessageKey } from '../i18n';
import { localizeAssetFieldLabel } from '../i18n/assetFieldLabels';
import { localizeServerMessage, formatRegisterHttpFailureMessage, formatRegisterUserAlreadyExistsMessage, isRegisterUserAlreadyExistsResponse, pickRegisterSubmittedContactIds, localizeValidationMessage } from '../i18n/localizeServerMessage';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { persistProfileContactHints } from '../utils/profileContactStorage';
import { persistProfileDetailsHints } from '../utils/profileDetailsStorage';
import {
  persistProfileCompletionLegal,
  readProfileCompletionLegal,
} from '../utils/profileCompletionLegalStorage';
import { persistIdentifierForLoginOption } from '../login/lastLoginStorage';
import {
  clearPersistedRegisterAttempt,
  loadPersistedRegisterAttempt,
  mergeRegisterAttemptWithEmpty,
  persistRegisterAttempt,
} from '../utils/authAttemptStorage';
import { fetchProfile } from '../api/profileApi';
import { patchSignedInProfileCompletion, persistSignedInProfileCompletionLocally } from '../utils/profileCompletionSave';
import {
  isPrivateClientProfileReadyForBooking,
  serverProfileStillLiteProvisioned,
  type PrivateClientBookingField,
} from '../utils/profileRegistrationComplete';
import { extractProfileDisplaySummary } from '../utils/profileDisplaySummary';
import { incompletePrivateClientUserInfoKeys } from '../utils/privateClientUserInfoProblems';
import {
  isPrivateClientRegisterFlow,
  mergePrivateClientRegisterEmptyPayload,
  missingPrivateClientRegisterRequiredField,
  preparePrivateClientRegisterPostBody,
  privateClientCompletionVisibleData,
  privateClientRegisterMandatoryKeys,
  privateClientRegisterShowsRegionField,
  privateClientRegisterVisibleData,
  withPrivateClientRegisterBackendDefaults,
} from '../utils/privateClientRegisterForm';
import {
  businessClientRegisterMandatoryKeys,
  businessClientRegisterVisibleData,
  isBusinessClientRegisterFlow,
  missingBusinessClientRegisterRequiredField,
  prepareBusinessClientRegisterPostBody,
} from '../utils/businessClientRegisterForm';
import {
  extractBusinessClientRegistrationIdentifierFromRegisterResponse,
} from '../utils/businessClientRegistrationIdentifier';
import { persistBusinessClientWorkerIdentifier } from '../utils/businessClientIdentifierStorage';
import {
  clearPendingBusinessClientLogo,
  persistPendingBusinessClientLogo,
} from '../utils/businessClientPendingLogoStorage';
import {
  businessWorkerRegisterMandatoryKeys,
  businessWorkerRegisterVisibleData,
  isBusinessWorkerRegisterFlow,
  missingBusinessWorkerRegisterRequiredField,
  prepareBusinessWorkerRegisterPostBody,
} from '../utils/businessWorkerRegisterForm';
import {
  assetOwnerRegisterMandatoryKeys,
  assetOwnerRegisterVisibleData,
  isAssetOwnerRegisterFlow,
  missingAssetOwnerRegisterRequiredField,
  prepareAssetOwnerRegisterPostBody,
  EMPTY_ASSET_OWNER_PAYOUT_BANK,
} from '../utils/assetOwnerRegisterForm';
import {
  filterAssetOwnerRegisterCountries,
  validateAssetOwnerPayoutFields,
} from '../utils/assetOwnerPayoutBank';
import {
  OWNER_LOGO_PICK_MAX_FILE_BYTES,
  OWNER_LOGO_REGISTER_PICK_MAX_WIRE_BYTES,
  assetOwnerRegisterPostWithinJsonLimit,
  ownerLogoMaxWireBytesForRegisterBody,
  prepareOwnerLogoForAssetOwnerRegister,
} from '../utils/profileOwnerLogoUpload';
import type { AppleIdTokenProfile } from '../login/appleSignIn';
import type { GoogleIdTokenProfile } from '../login/googleSignIn';
import styles from './RegisterTypeMenu.module.css';
import portalStyles from '../pages/AuthLoginPortal.module.css';

/** Root payload keys shown with a red * on labels in the register dynamic form. */
const REGISTER_MANDATORY_MARKER_KEYS: readonly string[] = [
  'email',
  'password',
  'confirm_password',
  'phone_number',
  'country_id',
  'state_id',
  'national_id',
  'gender',
];

const ASSET_OWNER_ROOT_FIELD_LABEL_KEYS: Partial<Record<string, MessageKey>> = {
  contact_first_name: 'register.contactFirstName',
  contact_last_name: 'register.contactLastName',
};

/**
 * Role is implied by the register path (e.g. `/register/BusinessClient` → business client).
 * Do not send `role_id` in the body — backend assigns it from the route.
 */
const REGISTER_BODY_OMIT_KEYS = ['confirm_password', 'role_id'] as const;

type RegisterTypeMeta = {
  endpoint: string;
  emptyPayload: Record<string, unknown>;
};

function registerFormPayloadForOption(option: RegisterOption): Record<string, unknown> {
  return mergeRegisterAttemptWithEmpty(
    REGISTER_META[option].emptyPayload,
    loadPersistedRegisterAttempt(option),
  );
}

const REGISTER_META: Record<RegisterOption, RegisterTypeMeta> = {
  'business client': {
    endpoint: 'BusinessClient',
    emptyPayload: {
      email: '',
      password: '',
      confirm_password: '',
      country_id: null,
      state_id: null,
      phone_number: '',
      business_id: '',
      business_name: '',
      business_type: '',
      contact_first_name: '',
      contact_last_name: '',
      credits_per_employee: null,
    },
  },
  'private client': {
    endpoint: 'PrivateClient',
    emptyPayload: mergePrivateClientRegisterEmptyPayload({
      email: '',
      password: '',
      confirm_password: '',
      country_id: null,
      phone_number: '',
      first_name: '',
      last_name: '',
      birth_date: '',
      occupation: '',
    }),
  },
  'business worker': {
    endpoint: 'BusinessWorker',
    emptyPayload: {
      business_client_identifier: '',
      email: '',
      password: '',
      confirm_password: '',
      phone_number: '',
      national_id: '',
      first_name: '',
      last_name: '',
      birth_date: '',
      position: '',
    },
  },
  'asset owner': {
    endpoint: 'AssetOwner',
    emptyPayload: {
      email: '',
      password: '',
      confirm_password: '',
      phone_number: '',
      phone_number_2: '',
      country_id: null,
      state_id: null,
      is_business: true,
      business_id: '',
      business_name: '',
      business_type: '',
      contact_first_name: '',
      contact_last_name: '',
      ...EMPTY_ASSET_OWNER_PAYOUT_BANK,
    },
  },
};

function generalInfoHeadersForRegister(
  userLoginType: string | null,
  _privateClientCompletionMode = false,
): Headers {
  const headers = new Headers({ Accept: 'application/json' });
  if (isBusinessClientLogin(userLoginType)) {
    const token = getSessionToken()?.trim();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }
  return headers;
}

export type RegisterTypeMenuProps = {
  /** Profile tab: business-worker-only flow for a signed-in business client. */
  profileBusinessWorkerOnly?: boolean;
  /** Booking gate: complete private-client registration while signed in. */
  privateClientCompletionMode?: boolean;
  /** When set with completion mode, only these fields are shown and patched. */
  completionMissingFields?: PrivateClientBookingField[];
  initialPayloadOverride?: Record<string, unknown>;
  onCompleted?: () => void;
  /** Lock registration to one account type (asset-owner portal, etc.). */
  fixedRegisterOption?: RegisterOption;
};

export function RegisterTypeMenu({
  profileBusinessWorkerOnly = false,
  privateClientCompletionMode = false,
  initialPayloadOverride,
  onCompleted,
  fixedRegisterOption,
}: RegisterTypeMenuProps = {}) {
  const { t } = useI18n();
  const { openAuth } = useAppNavigation();
  const { isLoggedIn, userLoginType, updateUserDisplayName } = useAuth();
  const [selectedOption, setSelectedOption] = useState<RegisterOption>(() => {
    if (fixedRegisterOption) {
      return fixedRegisterOption;
    }
    if (privateClientCompletionMode) {
      return 'private client';
    }
    return profileBusinessWorkerOnly ? BUSINESS_WORKER_REGISTER_OPTION : REGISTER_OPTIONS[0];
  });
  const [payload, setPayload] = useState<Record<string, unknown>>(() => {
    if (privateClientCompletionMode && initialPayloadOverride) {
      return deepClone(initialPayloadOverride);
    }
    const option = fixedRegisterOption
      ? fixedRegisterOption
      : profileBusinessWorkerOnly
        ? BUSINESS_WORKER_REGISTER_OPTION
        : REGISTER_OPTIONS[0];
    return registerFormPayloadForOption(option);
  });
  const [countries, setCountries] = useState<GeneralInfoPicklistItem[]>([]);
  const [countriesStatus, setCountriesStatus] = useState<GeneralInfoPicklistsProp['countriesStatus']>('idle');
  const [countriesError, setCountriesError] = useState<string | null>(null);
  const [regions, setRegions] = useState<GeneralInfoPicklistItem[]>([]);
  const [regionsStatus, setRegionsStatus] = useState<GeneralInfoPicklistsProp['regionsStatus']>('idle');
  const [regionsError, setRegionsError] = useState<string | null>(null);
  const [registrationFormOpen, setRegistrationFormOpen] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registerSucceeded, setRegisterSucceeded] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const [registeredBusinessClientWorkerCode, setRegisteredBusinessClientWorkerCode] = useState<
    string | null
  >(null);
  const [workerCodeCopied, setWorkerCodeCopied] = useState(false);
  const [completionSuccessMessage, setCompletionSuccessMessage] = useState<string | null>(null);
  const [failureSummary, setFailureSummary] = useState<string | null>(null);
  const [failureDetails, setFailureDetails] = useState<string | null>(null);
  const [formRemountKey, setFormRemountKey] = useState(0);
  const [googleRegisterCredential, setGoogleRegisterCredential] = useState<string | null>(null);
  const [appleRegisterCredential, setAppleRegisterCredential] = useState<string | null>(null);
  const oauthRegisterCredential = googleRegisterCredential || appleRegisterCredential;

  useEffect(() => {
    if (!isLoggedIn) {
      setGoogleRegisterCredential(null);
      setAppleRegisterCredential(null);
    }
  }, [isLoggedIn]);
  const [registerTermsOpen, setRegisterTermsOpen] = useState(false);
  const [registerTermsAgreed, setRegisterTermsAgreed] = useState(false);
  const [registerLegalOpen, setRegisterLegalOpen] = useState<ClientLegalAgreementState>(
    createEmptyClientLegalAgreementState,
  );
  const [registerLegalAgreed, setRegisterLegalAgreed] = useState<ClientLegalAgreementState>(() =>
    privateClientCompletionMode
      ? readProfileCompletionLegal(getStoredLoginIdentifier())
      : createEmptyClientLegalAgreementState(),
  );
  const [termsRequiredVisible, setTermsRequiredVisible] = useState(false);
  const businessLogoInputRef = useRef<HTMLInputElement>(null);
  const resultTopRef = useRef<HTMLDivElement>(null);
  const termsSectionRef = useRef<HTMLDivElement>(null);

  const businessLogoPreview = useMemo(() => {
    const raw = payload.logo;
    return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
  }, [payload.logo]);

  const onBusinessLogoPick = useCallback(() => {
    businessLogoInputRef.current?.click();
  }, []);

  const onBusinessLogoFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file || !file.type.startsWith('image/')) {
        return;
      }
      if (file.size > OWNER_LOGO_PICK_MAX_FILE_BYTES) {
        setError(t('register.businessLogoTooLarge'));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        void (async () => {
          if (typeof reader.result !== 'string') {
            return;
          }
          try {
            const compressed = await prepareOwnerLogoForAssetOwnerRegister(
              reader.result,
              OWNER_LOGO_REGISTER_PICK_MAX_WIRE_BYTES,
            );
            setPayload((prev) => ({ ...prev, logo: compressed }));
            setError(null);
          } catch {
            setError(t('register.businessLogoTooLargePayload'));
          }
        })();
      };
      reader.readAsDataURL(file);
    },
    [t],
  );

  const onBusinessLogoClear = useCallback(() => {
    setPayload((prev) => {
      const next = { ...prev };
      delete next.logo;
      delete next.logo_url;
      return next;
    });
  }, []);

  const businessClientRegistersWorkerOnly =
    isLoggedIn && isBusinessClientLogin(userLoginType);

  const showRegisterSocial =
    registrationFormOpen && !privateClientCompletionMode && !profileBusinessWorkerOnly;

  const submitDisabled = privateClientCompletionMode
    ? submitting
    : profileBusinessWorkerOnly
      ? submitting || !canSelectBusinessWorkerRegisterType(isLoggedIn, userLoginType)
      : submitting ||
        (isLoggedIn &&
          (!isBusinessClientLogin(userLoginType) ||
            selectedOption !== BUSINESS_WORKER_REGISTER_OPTION));

  const submitLabel = submitting
    ? t('common.submitting')
    : privateClientCompletionMode
      ? t('profile.completion.submit')
      : profileBusinessWorkerOnly
        ? t('profile.operation.registerWorker')
        : t('register.register');

  const onRegisterTermsAgreedChange = useCallback((agreed: boolean) => {
    setRegisterTermsAgreed(agreed);
    if (agreed) {
      setTermsRequiredVisible(false);
    }
  }, []);

  const onRegisterLegalToggleOpen = useCallback((documentId: ClientLegalDocumentId) => {
    setRegisterLegalOpen((current) => ({
      ...current,
      [documentId]: !current[documentId],
    }));
  }, []);

  const onRegisterLegalAgreedChange = useCallback(
    (documentId: ClientLegalDocumentId, agreed: boolean) => {
      setRegisterLegalAgreed((current) => {
        const next = { ...current, [documentId]: agreed };
        if (isClientLegalAgreementComplete(next)) {
          setTermsRequiredVisible(false);
        }
        if (privateClientCompletionMode) {
          persistProfileCompletionLegal(next, getStoredLoginIdentifier());
        }
        return next;
      });
    },
    [privateClientCompletionMode],
  );

  useEffect(() => {
    if (privateClientCompletionMode || profileBusinessWorkerOnly) {
      return;
    }
    if (!isLoggedIn) {
      setRegistrationFormOpen(true);
      return;
    }
    if (isBusinessClientLogin(userLoginType)) {
      setRegistrationFormOpen(true);
      return;
    }
    setRegistrationFormOpen(false);
  }, [isLoggedIn, userLoginType, privateClientCompletionMode, profileBusinessWorkerOnly]);

  const appliedCompletionPrefillRef = useRef(false);
  useEffect(() => {
    if (!privateClientCompletionMode || !initialPayloadOverride) {
      return;
    }
    if (appliedCompletionPrefillRef.current) {
      return;
    }
    appliedCompletionPrefillRef.current = true;
    setPayload(deepClone(initialPayloadOverride));
    setError(null);
    setRegisterSucceeded(false);
    setRegisteredEmail(null);
    setFailureSummary(null);
    setFailureDetails(null);
  }, [privateClientCompletionMode, initialPayloadOverride]);

  useEffect(() => {
    if (!businessClientRegistersWorkerOnly || !registrationFormOpen) {
      return;
    }
    if (selectedOption === BUSINESS_WORKER_REGISTER_OPTION) {
      return;
    }
    setSelectedOption(BUSINESS_WORKER_REGISTER_OPTION);
    setPayload(registerFormPayloadForOption(BUSINESS_WORKER_REGISTER_OPTION));
    setFormRemountKey((k) => k + 1);
    setError(null);
    setRegisterSucceeded(false);
    setRegisteredEmail(null);
    setFailureSummary(null);
    setFailureDetails(null);
  }, [businessClientRegistersWorkerOnly, registrationFormOpen, selectedOption]);

  const generalInfoPicklists: GeneralInfoPicklistsProp = useMemo(() => {
    const registerCountries =
      selectedOption === 'asset owner' ? filterAssetOwnerRegisterCountries(countries) : countries;
    return {
      countries: registerCountries,
      countriesStatus,
      countriesError,
      regions,
      regionsStatus,
      regionsError,
    };
  }, [countries, countriesStatus, countriesError, regions, regionsStatus, regionsError, selectedOption]);

  const privateClientRegisterFlow = isPrivateClientRegisterFlow(
    selectedOption,
    privateClientCompletionMode,
  );

  const businessClientRegisterFlow = isBusinessClientRegisterFlow(selectedOption);

  const businessWorkerRegisterFlow = isBusinessWorkerRegisterFlow(selectedOption);

  const businessWorkerSessionAuth = businessWorkerRegisterUsesBusinessClientSession(
    isLoggedIn,
    userLoginType,
  );

  const registerMandatoryMarkerKeys = useMemo(() => {
    if (privateClientCompletionMode) {
      const base = privateClientRegisterMandatoryKeys(payload.country_id).filter(
        (key) => key !== 'password' && key !== 'confirm_password',
      );
      return base;
    }
    return selectedOption === 'private client'
      ? privateClientRegisterMandatoryKeys(payload.country_id)
      : selectedOption === 'business client'
        ? businessClientRegisterMandatoryKeys(payload.country_id).filter(
            (key) =>
              !oauthRegisterCredential || (key !== 'password' && key !== 'confirm_password'),
          )
        : selectedOption === 'business worker'
          ? businessWorkerRegisterMandatoryKeys({
              requireBusinessClientIdentifier: !businessWorkerSessionAuth,
            })
          : selectedOption === 'asset owner'
          ? assetOwnerRegisterMandatoryKeys(payload.country_id).filter(
              (key) =>
                !oauthRegisterCredential || (key !== 'password' && key !== 'confirm_password'),
            )
          : REGISTER_MANDATORY_MARKER_KEYS;
  }, [
    privateClientCompletionMode,
    selectedOption,
    oauthRegisterCredential,
    payload.country_id,
    businessWorkerSessionAuth,
  ]);

  const assetOwnerRegisterFlow = isAssetOwnerRegisterFlow(selectedOption);
  const registerFlowWithOptionalLogo = assetOwnerRegisterFlow;
  const showBusinessLogoPicker = assetOwnerRegisterFlow || businessClientRegisterFlow;

  useEffect(() => {
    if (registerSucceeded && (assetOwnerRegisterFlow || businessClientRegisterFlow)) {
      resultTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [registerSucceeded, assetOwnerRegisterFlow, businessClientRegisterFlow]);

  const registerFormData = useMemo(
    () => {
      if (privateClientRegisterFlow) {
        return privateClientCompletionMode
          ? privateClientCompletionVisibleData(payload)
          : privateClientRegisterVisibleData(payload);
      }
      if (businessClientRegisterFlow) {
        return businessClientRegisterVisibleData(payload, {
          omitPasswordFields: Boolean(oauthRegisterCredential),
        });
      }
      if (businessWorkerRegisterFlow) {
        return businessWorkerRegisterVisibleData(payload, {
          omitBusinessClientIdentifier: businessWorkerSessionAuth,
        });
      }
      if (assetOwnerRegisterFlow) {
        return assetOwnerRegisterVisibleData(payload, {
          omitPasswordFields: Boolean(oauthRegisterCredential),
        });
      }
      return payload;
    },
    [
      assetOwnerRegisterFlow,
      businessClientRegisterFlow,
      businessWorkerRegisterFlow,
      businessWorkerSessionAuth,
      privateClientCompletionMode,
      privateClientRegisterFlow,
      payload,
      oauthRegisterCredential,
    ],
  );

  useEffect(() => {
    if (!registrationFormOpen) {
      return;
    }
    if (isLoggedIn && !isBusinessClientLogin(userLoginType) && !privateClientCompletionMode) {
      return;
    }
    if (businessWorkerRegisterFlow) {
      return;
    }
    let cancelled = false;

    async function load() {
      setCountriesStatus('loading');
      setCountriesError(null);
      try {
        const list = await loadGeneralInfoCountries(
          generalInfoHeadersForRegister(userLoginType, privateClientCompletionMode),
        );
        if (cancelled) {
          return;
        }
        setCountries(list);
        setCountriesStatus('ready');
      } catch (err) {
        if (!cancelled) {
          setCountries([]);
          setCountriesStatus('error');
          setCountriesError(err instanceof Error ? err.message : 'Failed to load countries');
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [
    registrationFormOpen,
    isLoggedIn,
    userLoginType,
    privateClientCompletionMode,
    businessWorkerRegisterFlow,
  ]);

  useEffect(() => {
    if (!registrationFormOpen) {
      return;
    }
    if (isLoggedIn && !isBusinessClientLogin(userLoginType) && !privateClientCompletionMode) {
      return;
    }
    if (businessWorkerRegisterFlow) {
      return;
    }
    const rawCountry = payload.country_id;
    if (typeof rawCountry !== 'number' || !Number.isFinite(rawCountry)) {
      setRegions([]);
      setRegionsStatus('idle');
      setRegionsError(null);
      return;
    }
    const countryId: number = rawCountry;

    let cancelled = false;

    async function load() {
      setRegionsStatus('loading');
      setRegionsError(null);
      try {
        const list = await loadGeneralInfoRegions(
          countryId,
          generalInfoHeadersForRegister(userLoginType, privateClientCompletionMode),
        );
        if (!cancelled) {
          setRegions(list);
          setRegionsStatus('ready');
          setPayload((prev) => {
            const sid = prev.state_id;
            if (list.length === 0) {
              return setAtPath(prev, ['state_id'], null);
            }
            if (typeof sid === 'number' && !list.some((r) => r.id === sid)) {
              return setAtPath(prev, ['state_id'], null);
            }
            return prev;
          });
        }
      } catch (err) {
        if (!cancelled) {
          setRegions([]);
          setRegionsStatus('error');
          setRegionsError(err instanceof Error ? err.message : 'Failed to load regions');
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [
    registrationFormOpen,
    isLoggedIn,
    userLoginType,
    payload.country_id,
    privateClientCompletionMode,
    businessWorkerRegisterFlow,
  ]);

  const onOptionChange = (option: RegisterOption) => {
    setSelectedOption(option);
    setPayload(registerFormPayloadForOption(option));
    setGoogleRegisterCredential(null);
    setAppleRegisterCredential(null);
    setFormRemountKey((k) => k + 1);
    setError(null);
    setRegisterSucceeded(false);
    setRegisteredEmail(null);
    setFailureSummary(null);
    setFailureDetails(null);
    setRegisterTermsAgreed(false);
    setRegisterTermsOpen(false);
    setRegisterLegalAgreed(createEmptyClientLegalAgreementState());
    setRegisterLegalOpen(createEmptyClientLegalAgreementState());
    setTermsRequiredVisible(false);
  };

  const onFieldChange = useCallback((path: string[], value: unknown) => {
    setPayload((prev) => {
      let next = setAtPath(prev, path, value);
      if (path.length === 1 && path[0] === 'country_id') {
        next = setAtPath(next, ['state_id'], null);
      }
      return next;
    });
  }, []);

  const onClearFields = useCallback(() => {
    clearPersistedRegisterAttempt(selectedOption);
    setPayload(deepClone(REGISTER_META[selectedOption].emptyPayload));
    setGoogleRegisterCredential(null);
    setAppleRegisterCredential(null);
    setFormRemountKey((k) => k + 1);
    setError(null);
    setRegisterSucceeded(false);
    setRegisteredEmail(null);
    setFailureSummary(null);
    setFailureDetails(null);
    setRegisterTermsAgreed(false);
    setRegisterTermsOpen(false);
    setRegisterLegalAgreed(createEmptyClientLegalAgreementState());
    setRegisterLegalOpen(createEmptyClientLegalAgreementState());
    setTermsRequiredVisible(false);
  }, [selectedOption]);

  const onClearButtonClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onClearFields();
    },
    [onClearFields],
  );

  const onAssetOwnerPayoutChange = useCallback((patch: Record<string, unknown>) => {
    setPayload((current) => ({ ...current, ...patch }));
  }, []);

  const applyGoogleRegisterPrefill = useCallback((profile: GoogleIdTokenProfile, credential: string) => {
    setGoogleRegisterCredential(credential);
    setAppleRegisterCredential(null);
    setPayload((prev) => {
      const next = { ...prev };
      if (profile.email) {
        next.email = profile.email;
      }
      if (profile.firstName) {
        next.contact_first_name = profile.firstName;
      }
      if (profile.lastName) {
        next.contact_last_name = profile.lastName;
      }
      const businessName =
        typeof next.business_name === 'string' ? next.business_name.trim() : '';
      if (!businessName && profile.fullName) {
        next.business_name = profile.fullName;
      }
      return next;
    });
    setFormRemountKey((k) => k + 1);
    setError(null);
    setRegisterSucceeded(false);
    setRegisteredEmail(null);
    setFailureSummary(null);
    setFailureDetails(null);
  }, []);

  const applyAppleRegisterPrefill = useCallback((profile: AppleIdTokenProfile, credential: string) => {
    setAppleRegisterCredential(credential);
    setGoogleRegisterCredential(null);
    setPayload((prev) => {
      const next = { ...prev };
      if (profile.email) {
        next.email = profile.email;
      }
      if (profile.firstName) {
        next.contact_first_name = profile.firstName;
      }
      if (profile.lastName) {
        next.contact_last_name = profile.lastName;
      }
      const businessName =
        typeof next.business_name === 'string' ? next.business_name.trim() : '';
      if (!businessName && profile.fullName) {
        next.business_name = profile.fullName;
      }
      return next;
    });
    setFormRemountKey((k) => k + 1);
    setError(null);
    setRegisterSucceeded(false);
    setRegisteredEmail(null);
    setFailureSummary(null);
    setFailureDetails(null);
  }, []);

  const registerReadonlyPathPredicate = useCallback(
    (pathSegments: readonly string[]) =>
      Boolean(oauthRegisterCredential) &&
      pathSegments.length === 1 &&
      pathSegments[0] === 'email',
    [oauthRegisterCredential],
  );

  const onGoToSignInAfterRegister = useCallback(() => {
    if (fixedRegisterOption === 'asset owner' || selectedOption === 'asset owner') {
      openAuth('login-asset-owner');
      return;
    }
    if (selectedOption === 'private client') {
      openAuth('login');
      return;
    }
    openAuth('login-business-owner');
  }, [fixedRegisterOption, openAuth, selectedOption]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!registrationFormOpen) {
      return;
    }

    const registerTermsComplete = assetOwnerRegisterFlow
      ? registerTermsAgreed
      : isClientLegalAgreementComplete(registerLegalAgreed);

    if (!registerTermsComplete) {
      setTermsRequiredVisible(true);
      setError(
        assetOwnerRegisterFlow ? t('platform.termsRequired') : t('platform.termsRequiredAll'),
      );
      setRegisterSucceeded(false);
      setFailureSummary(null);
      setFailureDetails(null);
      termsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setTermsRequiredVisible(false);

    if (!privateClientCompletionMode) {
      persistRegisterAttempt(selectedOption, payload);
    }

    if (selectedOption === BUSINESS_WORKER_REGISTER_OPTION) {
      if (isLoggedIn && !isBusinessClientLogin(userLoginType)) {
        setError(
          'Sign in as a business client to register a business worker from your profile, or log out and use the employer code on the public register page.',
        );
        return;
      }
      if (businessWorkerSessionAuth) {
        const headersProbe = await buildRegisterSubmitHeadersAsync(
          selectedOption,
          userLoginType,
          privateClientCompletionMode,
          isLoggedIn,
        );
        if (!headersProbe.get('Authorization')) {
          setFailureSummary(t('register.error.businessWorkerSessionRequired'));
          setFailureDetails(null);
          return;
        }
      }
    } else if (isLoggedIn && !privateClientCompletionMode) {
      return;
    }

    if (assetOwnerRegisterFlow) {
      const payoutErrorKey = validateAssetOwnerPayoutFields(payload);
      if (payoutErrorKey) {
        setError(t(payoutErrorKey));
        setRegisterSucceeded(false);
        setFailureSummary(null);
        setFailureDetails(null);
        return;
      }
      const missingRequired = missingAssetOwnerRegisterRequiredField(payload, {
        skipPassword: Boolean(oauthRegisterCredential),
      });
      if (missingRequired) {
        setError(
          t('register.error.fieldRequired', {
            field: localizeAssetFieldLabel(missingRequired, t),
          }),
        );
        setRegisterSucceeded(false);
        setFailureSummary(null);
        setFailureDetails(null);
        return;
      }
    }

    if (businessClientRegisterFlow) {
      const missingRequired = missingBusinessClientRegisterRequiredField(payload, {
        skipPassword: Boolean(oauthRegisterCredential),
      });
      if (missingRequired) {
        setError(
          t('register.error.fieldRequired', {
            field: localizeAssetFieldLabel(missingRequired, t),
          }),
        );
        setRegisterSucceeded(false);
        setFailureSummary(null);
        setFailureDetails(null);
        return;
      }
    }

    if (privateClientRegisterFlow && !privateClientCompletionMode) {
      const missingRequired = missingPrivateClientRegisterRequiredField(payload, {
        skipPassword: false,
      });
      if (missingRequired) {
        setError(
          t('register.error.fieldRequired', {
            field: localizeAssetFieldLabel(missingRequired, t),
          }),
        );
        setRegisterSucceeded(false);
        setFailureSummary(null);
        setFailureDetails(null);
        return;
      }
    }

    if (privateClientCompletionMode) {
      const missingRequired = missingPrivateClientRegisterRequiredField(payload, {
        skipPassword: true,
      });
      if (missingRequired) {
        setError(
          t('register.error.fieldRequired', {
            field: localizeAssetFieldLabel(missingRequired, t),
          }),
        );
        setRegisterSucceeded(false);
        setFailureSummary(null);
        setFailureDetails(null);
        return;
      }
    }

    if (businessWorkerRegisterFlow) {
      const missingRequired = missingBusinessWorkerRegisterRequiredField(payload, {
        requireBusinessClientIdentifier: !businessWorkerSessionAuth,
      });
      if (missingRequired) {
        setError(
          t('register.error.fieldRequired', {
            field: localizeAssetFieldLabel(missingRequired, t),
          }),
        );
        setRegisterSucceeded(false);
        setFailureSummary(null);
        setFailureDetails(null);
        return;
      }
    }

    const password = payload.password;
    const confirmPassword = payload.confirm_password;
    const skipPasswordValidation =
      privateClientCompletionMode ||
      ((assetOwnerRegisterFlow || businessClientRegisterFlow) && Boolean(oauthRegisterCredential));
    if (!skipPasswordValidation) {
      if (typeof password !== 'string' || typeof confirmPassword !== 'string') {
        setError('Enter password and confirmation.');
        setRegisterSucceeded(false);
        setFailureSummary(null);
        setFailureDetails(null);
        return;
      }
      if (password !== confirmPassword) {
        setError('Password and confirmation must match.');
        setRegisterSucceeded(false);
        setFailureSummary(null);
        setFailureDetails(null);
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    setRegisterSucceeded(false);
    setRegisteredEmail(null);
    setRegisteredBusinessClientWorkerCode(null);
    setWorkerCodeCopied(false);
    setFailureSummary(null);
    setFailureDetails(null);

    try {
      let submitPayload = { ...payload };

      const registerFieldsWithoutLogo = registerFlowWithOptionalLogo
        ? (() => {
            const withoutLogo = { ...submitPayload };
            delete withoutLogo.logo;
            delete withoutLogo.logo_url;
            const prepared = assetOwnerRegisterFlow
              ? prepareAssetOwnerRegisterPostBody(
                  withoutLogo,
                  googleRegisterCredential,
                  'asset owner',
                  regions,
                  appleRegisterCredential,
                )
              : prepareBusinessClientRegisterPostBody(
                  withoutLogo,
                  googleRegisterCredential,
                  'business client',
                  regions,
                  appleRegisterCredential,
                );
            for (const key of REGISTER_BODY_OMIT_KEYS) {
              delete prepared[key];
            }
            delete prepared.logo;
            delete prepared.logo_url;
            return prepared;
          })()
        : null;

      if (
        registerFlowWithOptionalLogo &&
        typeof submitPayload.logo === 'string' &&
        submitPayload.logo.startsWith('data:image/')
      ) {
        try {
          const maxLogoWireBytes = ownerLogoMaxWireBytesForRegisterBody(
            registerFieldsWithoutLogo ?? {},
          );
          const compressedLogo = await prepareOwnerLogoForAssetOwnerRegister(
            submitPayload.logo,
            maxLogoWireBytes,
          );
          submitPayload = {
            ...submitPayload,
            logo: compressedLogo,
          };
        } catch {
          setError(t('register.businessLogoTooLargePayload'));
          setSubmitting(false);
          return;
        }
      }

      const registerFields =
        privateClientRegisterFlow && !privateClientCompletionMode
          ? preparePrivateClientRegisterPostBody({ ...submitPayload }, regions)
          : privateClientRegisterFlow
            ? withPrivateClientRegisterBackendDefaults({ ...submitPayload }, regions)
            : businessClientRegisterFlow
              ? prepareBusinessClientRegisterPostBody(
                  { ...submitPayload },
                  googleRegisterCredential,
                  'business client',
                  regions,
                  appleRegisterCredential,
                )
              : businessWorkerRegisterFlow
                ? prepareBusinessWorkerRegisterPostBody({ ...submitPayload })
                : assetOwnerRegisterFlow
                ? prepareAssetOwnerRegisterPostBody(
                    { ...submitPayload },
                    googleRegisterCredential,
                    'asset owner',
                    regions,
                    appleRegisterCredential,
                  )
                : { ...submitPayload };
      for (const key of REGISTER_BODY_OMIT_KEYS) {
        delete registerFields[key];
      }
      if (
        !registerSubmitNeedsSessionAuth(
          selectedOption,
          userLoginType,
          privateClientCompletionMode,
          isLoggedIn,
        )
      ) {
        stripRegisterWireAuthFields(registerFields);
      }

      if (businessClientRegisterFlow) {
        delete registerFields.logo;
        delete registerFields.logo_url;
        delete registerFields.registration_date;
      }

      if (registerFlowWithOptionalLogo) {
        const registerBodyPreview: Record<string, unknown> = businessClientRegisterFlow
          ? { ...registerFields }
          : {
              ...registerFields,
              registration_date: new Date().toISOString(),
            };
        const wireBody = prepareRegisterRequestBody(registerBodyPreview);
        if (
          wireBody !== null &&
          typeof wireBody === 'object' &&
          !Array.isArray(wireBody) &&
          !assetOwnerRegisterPostWithinJsonLimit(wireBody as Record<string, unknown>)
        ) {
          setError(t('register.businessLogoTooLargePayload'));
          setSubmitting(false);
          return;
        }
      }

      if (privateClientCompletionMode) {
        persistProfileCompletionLegal(registerLegalAgreed, getStoredLoginIdentifier());
        const patchResult = await patchSignedInProfileCompletion({ ...payload });
        if (!patchResult.savedToServer && !patchResult.identityConflict) {
          throw new Error(t('profile.completion.saveFailed'));
        }
        persistSignedInProfileCompletionLocally(payload);
        const first = typeof payload.first_name === 'string' ? payload.first_name.trim() : '';
        const last = typeof payload.last_name === 'string' ? payload.last_name.trim() : '';
        const joined = [first, last].filter(Boolean).join(' ').trim();
        if (joined) {
          updateUserDisplayName(joined);
        }
        if (!patchResult.savedToServer) {
          setCompletionSuccessMessage(t('profile.completion.successLocalOnly'));
          setError(t('profile.completion.serverStillIncomplete'));
          setRegisterSucceeded(false);
          setSubmitting(false);
          return;
        }
        const submittedUserInfoComplete = incompletePrivateClientUserInfoKeys(payload).length === 0;
        try {
          const profileData = await fetchProfile();
          const loginId = getStoredLoginIdentifier();
          const summary = extractProfileDisplaySummary(
            profileData,
            joined || null,
            loginId,
            getSessionToken(),
          );
          if (
            !submittedUserInfoComplete &&
            (serverProfileStillLiteProvisioned(profileData) ||
              !isPrivateClientProfileReadyForBooking(profileData, summary, loginId))
          ) {
            setError(t('profile.completion.serverStillIncomplete'));
            setRegisterSucceeded(false);
            setSubmitting(false);
            return;
          }
        } catch {
          /* PATCH succeeded; continue even if GET /profile is briefly stale */
        }
        if (patchResult.identityConflict) {
          setCompletionSuccessMessage(t('profile.completion.successPartial'));
        } else {
          setCompletionSuccessMessage(t('profile.completion.success'));
        }
        setRegisterSucceeded(true);
        onCompleted?.();
        return;
      }

      const headers = await buildRegisterSubmitHeadersAsync(
        selectedOption,
        userLoginType,
        privateClientCompletionMode,
        isLoggedIn,
      );
      const withRegistrationDate: Record<string, unknown> = businessClientRegisterFlow
        ? { ...registerFields }
        : {
            ...registerFields,
            registration_date: new Date().toISOString(),
          };
      const registerPathTail = `register/${REGISTER_META[selectedOption].endpoint}`;
      const wireBodyJson = JSON.stringify(prepareRegisterRequestBody(withRegistrationDate));
      const { response, urlUsed: registerUrlUsed } = await fetchWithDevProxyFallback(
        registerPathTail,
        buildRegisterPostInit(
          headers,
          wireBodyJson,
          selectedOption,
          userLoginType,
          privateClientCompletionMode,
          isLoggedIn,
        ),
        devProxyRouteForApiPath(registerPathTail),
      );

      const responseText = await response.text();

      if (response.ok) {
        const registrationEmail =
          typeof registerFields.email === 'string'
            ? registerFields.email.trim()
            : typeof payload.email === 'string'
              ? payload.email.trim()
              : '';
        const registrationPhone =
          typeof registerFields.phone_number === 'string'
            ? registerFields.phone_number.trim()
            : typeof payload.phone_number === 'string'
              ? payload.phone_number.trim()
              : '';
        persistProfileContactHints(
          registrationEmail || null,
          registrationPhone || null,
          registrationEmail || registrationPhone || undefined,
        );
        const registrationFirstName =
          (typeof payload.contact_first_name === 'string' && payload.contact_first_name.trim()) ||
          (typeof payload.first_name === 'string' && payload.first_name.trim()) ||
          '';
        const registrationLastName =
          (typeof payload.contact_last_name === 'string' && payload.contact_last_name.trim()) ||
          (typeof payload.last_name === 'string' && payload.last_name.trim()) ||
          '';
        if (registrationFirstName || registrationLastName) {
          persistProfileDetailsHints(
            {
              firstName: registrationFirstName || null,
              lastName: registrationLastName || null,
            },
            registrationEmail || registrationPhone || undefined,
          );
        }
        if (
          businessClientRegisterFlow &&
          typeof submitPayload.logo === 'string' &&
          submitPayload.logo.startsWith('data:image/')
        ) {
          persistPendingBusinessClientLogo(
            submitPayload.logo,
            registrationEmail || registrationPhone || undefined,
          );
        }
        if (privateClientCompletionMode) {
          setRegisterSucceeded(true);
          onCompleted?.();
        } else if (profileBusinessWorkerOnly) {
          setRegisterSucceeded(true);
        } else {
          const email =
            typeof registerFields.email === 'string'
              ? registerFields.email.trim()
              : typeof payload.email === 'string'
                ? payload.email.trim()
                : '';
          if (email) {
            persistIdentifierForLoginOption(selectedOption, email);
            persistProfileContactHints(email, null);
          }
          setRegisteredEmail(email || null);
          if (businessClientRegisterFlow) {
            const workerCode =
              extractBusinessClientRegistrationIdentifierFromRegisterResponse(responseText);
            if (workerCode) {
              persistBusinessClientWorkerIdentifier(workerCode);
              setRegisteredBusinessClientWorkerCode(workerCode);
            } else {
              setRegisteredBusinessClientWorkerCode(null);
            }
          } else {
            setRegisteredBusinessClientWorkerCode(null);
          }
          setRegisterSucceeded(true);
        }
      } else {
        const wireBody = prepareRegisterRequestBody(withRegistrationDate);
        const submittedRecord =
          typeof wireBody === 'object' && wireBody !== null && !Array.isArray(wireBody)
            ? (wireBody as Record<string, unknown>)
            : withRegistrationDate;
        const failureMessage =
          response.status === 413 && registerFlowWithOptionalLogo
            ? t('register.businessLogoTooLargePayload')
            : isRegisterUserAlreadyExistsResponse(response.status, responseText)
              ? formatRegisterUserAlreadyExistsMessage(
                  t,
                  pickRegisterSubmittedContactIds(submittedRecord),
                  {
                    googleLockedEmail: Boolean(oauthRegisterCredential),
                    registerOption: selectedOption,
                  },
                )
              : formatRegisterHttpFailureMessage(response.status, responseText, t, {
                  businessWorkerPublicRegister:
                    businessWorkerRegisterFlow && !businessWorkerSessionAuth,
                  businessWorkerSessionRegister:
                    businessWorkerRegisterFlow && businessWorkerSessionAuth,
                });
        setFailureSummary(failureMessage);
        setFailureDetails(
          formatHttpErrorDetails({
            requestUrl: registerUrlUsed,
            requestPayload: withRegistrationDate,
            response,
            responseText,
          }),
        );
      }
    } catch (submitError) {
      if (submitError instanceof AssetRequestError) {
        setFailureSummary(localizeServerMessage(submitError.message, t));
        setFailureDetails(submitError.rawBody ?? submitError.networkDetails ?? null);
      } else if (submitError instanceof Error) {
        setError(localizeValidationMessage(submitError.message, t));
      } else {
        setError(t('register.error.requestFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onCopyWorkerCode = useCallback(async () => {
    const code = registeredBusinessClientWorkerCode?.trim();
    if (!code) {
      return;
    }
    try {
      await navigator.clipboard.writeText(code);
      setWorkerCodeCopied(true);
    } catch {
      setWorkerCodeCopied(false);
    }
  }, [registeredBusinessClientWorkerCode]);

  const containerClass = [
    styles.container,
    profileBusinessWorkerOnly ? styles.containerInProfile : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <form
      className={containerClass}
      onSubmit={onSubmit}
      autoComplete="off"
    >
      {(registerSucceeded || failureSummary || error) ? (
        <div ref={resultTopRef} className={styles.resultTop}>
          {registerSucceeded ? (
            <div className={styles.successPanel}>
              <p className={styles.successMessage} role="status">
                {privateClientCompletionMode && completionSuccessMessage
                  ? completionSuccessMessage
                  : privateClientCompletionMode
                    ? t('profile.completion.success')
                    : registeredEmail
                      ? t('register.successWithEmail', { email: registeredEmail })
                      : t('register.success')}
              </p>
              {!privateClientCompletionMode &&
              registeredEmail &&
              selectedOption !== 'private client' ? (
                <p className={styles.successHint}>
                  {t('register.successOtherSignInHint', {
                    type: translateLoginOption(selectedOption, t),
                  })}
                </p>
              ) : null}
              {registeredBusinessClientWorkerCode ? (
                <div className={styles.workerCodePanel}>
                  <p className={styles.successHint}>
                    {t('register.successBusinessClientWorkerCode', {
                      code: registeredBusinessClientWorkerCode,
                    })}
                  </p>
                  <p className={styles.workerCodeValue} aria-label={t('register.businessClientWorkerCode')}>
                    {registeredBusinessClientWorkerCode}
                  </p>
                  <p className={styles.successHint}>{t('register.successBusinessClientWorkerCodeHint')}</p>
                  <button
                    type="button"
                    className={styles.copyWorkerCodeBtn}
                    onClick={() => {
                      void onCopyWorkerCode();
                    }}
                  >
                    {workerCodeCopied ? t('register.copyWorkerCodeDone') : t('register.copyWorkerCode')}
                  </button>
                </div>
              ) : businessClientRegisterFlow && !privateClientCompletionMode ? (
                <p className={styles.successHint}>{t('register.successBusinessClientWorkerCodePending')}</p>
              ) : null}
              {!privateClientCompletionMode ? (
                <button
                  type="button"
                  className={styles.goToSignInBtn}
                  onClick={onGoToSignInAfterRegister}
                >
                  {t('register.goToSignIn')}
                </button>
              ) : null}
            </div>
          ) : null}
          {failureSummary && !registerSucceeded ? (
            <div className={styles.failureBlock} role="alert">
              <p className={styles.failureSummary}>{failureSummary}</p>
            </div>
          ) : null}
          {error && !registerSucceeded && !failureSummary ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}

      {!registerSucceeded ? (
        <>
      <div className={styles.controls}>
        {!profileBusinessWorkerOnly && !privateClientCompletionMode && !fixedRegisterOption ? (
          <select
            id="register-type"
            aria-label={t('register.registerType')}
            className={styles.select}
            value={selectedOption}
            onChange={(event) => onOptionChange(event.target.value as RegisterOption)}
            disabled={isLoggedIn && !isBusinessClientLogin(userLoginType)}
          >
            {REGISTER_OPTIONS.map((option) => {
              const workerLockedOut =
                option === BUSINESS_WORKER_REGISTER_OPTION &&
                isLoggedIn &&
                !isBusinessClientLogin(userLoginType);
              const hideNonWorkerWhileBusinessClient =
                businessClientRegistersWorkerOnly && option !== BUSINESS_WORKER_REGISTER_OPTION;
              const optionDisabled = workerLockedOut || hideNonWorkerWhileBusinessClient;
              return (
                <option
                  key={option}
                  value={option}
                  disabled={optionDisabled}
                  title={
                    workerLockedOut
                      ? 'Log out to register as a business worker with an employer code, or sign in as a business client to add workers from your profile.'
                      : hideNonWorkerWhileBusinessClient
                        ? 'While signed in as a business client, only business worker registration is available.'
                        : undefined
                  }
                >
                  {translateLoginOption(option, t)}
                </option>
              );
            })}
          </select>
        ) : null}
        <button
          type="submit"
          className={styles.submit}
          disabled={submitDisabled}
          title={
            submitting
              ? undefined
              : submitDisabled && isLoggedIn && !isBusinessClientLogin(userLoginType)
                ? 'Sign in as a business client to register a worker, or log out to register other account types.'
                : undefined
          }
        >
          {submitLabel}
        </button>
        {registrationFormOpen && !privateClientCompletionMode ? (
          <button
            type="button"
            className={styles.clearButton}
            onClick={onClearButtonClick}
            onMouseDown={(e) => e.stopPropagation()}
            title={t('common.clear')}
          >
            {t('common.clear')}
          </button>
        ) : null}
      </div>

      {registrationFormOpen ? (
        <div className={styles.formPanel}>
          {showRegisterSocial ? (
            <div className={`${styles.registerGoogleWrap} ${portalStyles.socialStack}`}>
              <AuthGoogleSignInBlock
                loginType={selectedOption}
                introKey={
                  assetOwnerRegisterFlow
                    ? 'auth.register.assetOwnerGoogleHint'
                    : 'auth.register.googleHint'
                }
                registerPrefillOnMiss={assetOwnerRegisterFlow}
                onRegisterPrefill={assetOwnerRegisterFlow ? applyGoogleRegisterPrefill : undefined}
              />
              <AuthAppleSignInBlock
                loginType={selectedOption}
                introKey={
                  assetOwnerRegisterFlow
                    ? 'auth.register.assetOwnerAppleHint'
                    : 'auth.register.appleHint'
                }
                registerPrefillOnMiss={assetOwnerRegisterFlow}
                onRegisterPrefill={assetOwnerRegisterFlow ? applyAppleRegisterPrefill : undefined}
              />
            </div>
          ) : null}
          {businessWorkerRegisterFlow && !businessWorkerSessionAuth ? (
            <p className={styles.workerCodeFormHint}>{t('register.businessWorkerCodeHint')}</p>
          ) : null}
          {privateClientCompletionMode ? <ProfileCompletionUserInfo payload={payload} /> : null}
          <DynamicRecordForm
            theme="light"
            remountKey={`${selectedOption}-${formRemountKey}`}
            data={registerFormData}
            onFieldChange={onFieldChange}
            idPrefix={`register-${formRemountKey}`}
            showPasswordRequirements={!privateClientCompletionMode && !oauthRegisterCredential}
            generalInfoPicklists={generalInfoPicklists}
            mandatoryMarkerKeys={registerMandatoryMarkerKeys}
            rootFieldLabelKeys={
              assetOwnerRegisterFlow ? ASSET_OWNER_ROOT_FIELD_LABEL_KEYS : undefined
            }
            readonlyPathPredicate={
              assetOwnerRegisterFlow ? registerReadonlyPathPredicate : undefined
            }
            birthDateDayMonthYear={
              selectedOption === 'private client' || selectedOption === 'business worker'
            }
          />
          {assetOwnerRegisterFlow ? (
            <AssetOwnerBankDetailsSection
              payload={payload}
              disabled={submitting}
              onChange={onAssetOwnerPayoutChange}
            />
          ) : null}
          {showBusinessLogoPicker ? (
            <div className={styles.businessLogoPlaceholder}>
              <span className={styles.businessLogoLabel}>{t('register.businessLogo')}</span>
              {businessClientRegisterFlow ? (
                <p className={styles.businessLogoHint}>{t('register.businessLogoAfterSignIn')}</p>
              ) : null}
              <input
                ref={businessLogoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className={styles.businessLogoFileInput}
                tabIndex={-1}
                aria-hidden
                onChange={onBusinessLogoFileChange}
              />
              {businessLogoPreview ? (
                <div className={styles.businessLogoPreviewRow}>
                  <img
                    src={businessLogoPreview}
                    alt=""
                    className={styles.businessLogoPreviewImg}
                  />
                  <div className={styles.businessLogoPreviewActions}>
                    <button
                      type="button"
                      className={styles.businessLogoChangeBtn}
                      onClick={onBusinessLogoPick}
                      disabled={submitting}
                    >
                      {t('register.businessLogoChange')}
                    </button>
                    <button
                      type="button"
                      className={styles.businessLogoClearBtn}
                      onClick={onBusinessLogoClear}
                      disabled={submitting}
                    >
                      {t('register.businessLogoRemove')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.businessLogoBtn}
                  onClick={onBusinessLogoPick}
                  disabled={submitting}
                >
                  {t('register.businessLogoAdd')}
                </button>
              )}
            </div>
          ) : null}
          <div ref={termsSectionRef}>
            {assetOwnerRegisterFlow ? (
              <PlatformTermsAcceptSection
                idPrefix={`register-${formRemountKey}`}
                open={registerTermsOpen}
                agreed={registerTermsAgreed}
                theme="light"
                disabled={submitting}
                termsVariant="assetOwner"
                onToggleOpen={() => setRegisterTermsOpen((value) => !value)}
                onAgreedChange={onRegisterTermsAgreedChange}
              />
            ) : (
              <PlatformLegalDocumentsAcceptSection
                idPrefix={`register-${formRemountKey}`}
                open={registerLegalOpen}
                agreed={registerLegalAgreed}
                theme="light"
                disabled={submitting}
                onToggleOpen={onRegisterLegalToggleOpen}
                onAgreedChange={onRegisterLegalAgreedChange}
              />
            )}
            {termsRequiredVisible ? (
              <p className={styles.termsRequiredError} role="alert">
                {assetOwnerRegisterFlow
                  ? t('platform.termsRequired')
                  : t('platform.termsRequiredAll')}
              </p>
            ) : null}
          </div>
          {registrationFormOpen ? (
            <div className={styles.bottomSubmitRow}>
              <button
                type="submit"
                className={styles.submit}
                disabled={submitDisabled}
                title={
                  submitting
                    ? undefined
                    : submitDisabled && isLoggedIn && !isBusinessClientLogin(userLoginType)
                      ? 'Sign in as a business client to register a worker, or log out to register other account types.'
                      : undefined
                }
              >
                {submitLabel}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
        </>
      ) : null}
    </form>
  );
}
